/**
 * W28-G #273: caption continuity across reconnect/resume.
 *
 * LiveKit’s useTranscriptions() is per-connection â on a resumed session
 * the prior captions vanish. Simurgh persists every turn to Neo4j, so we
 * hydrate the caption panel from the existing
 * GET /api/simurgh/conversations/:conversationId endpoint and render the
 * prior turns above the live segments, deduped against them.
 */

// #1302 Guard B: the persisted-transcript fetch is a brand endpoint adapter
// (LMC forwards to simurghService.getConversation) via BrandChatConfig.
import { chatConfig } from '../config';

export interface PriorCaption {
  id: string;
  text: string;
  isUser: boolean;
}

/** Strip internal [STATE:{...}] blocks from persisted assistant turns. */
const stripStateBlocks = (text: string): string =>
  text.replace(/\[STATE:\{[^\]]*\}\]/g, '').trim();

/**
 * Fetch the persisted transcript for a resumed conversation and map it
 * into caption entries. Returns [] on any failure — hydration is a
 * best-effort continuity aid, never a blocker for the live session.
 */
export async function fetchPriorCaptions(conversationId: string): Promise<PriorCaption[]> {
  try {
    const conversation = await chatConfig().endpoints.getVoiceConversation(conversationId);
    const messages = conversation.messages || [];
    return messages
      .map((msg, index) => ({
        id: msg.messageId || `prior-${index}`,
        text: stripStateBlocks(msg.content || ''),
        isUser: msg.role === 'user',
      }))
      .filter((caption) => caption.text.length > 0);
  } catch {
    return [];
  }
}

/**
 * Drop hydrated captions whose text also appears as a live segment —
 * guards against double-rendering a turn that was persisted just before
 * the reconnect and is also re-delivered live.
 */
export function dedupHydrated(hydrated: PriorCaption[], liveTexts: string[]): PriorCaption[] {
  if (liveTexts.length === 0) return hydrated;
  const live = new Set(liveTexts.map((t) => t.trim()));
  return hydrated.filter((caption) => !live.has(caption.text));
}
